import { Subject } from 'rxjs';

import { ItemFlatNode, ItemNode } from '../models/item';
import { MaterialTree } from '../models/material-tree';

export class TreeUtils {
    selectedItemsChange = new Subject<ItemNode[]>()

    private nodeHt = {};
    private childrenSelectedCount = {};

    constructor(private materialTree: MaterialTree) {
        this.materialTree.treeControl.dataNodes.forEach(
            node => {
                this.nodeHt[node.Id] = node;
            }
        );
    }

    hasChild = (_: number, nodeData: ItemFlatNode) => (nodeData.expandable);

    isRadio = (_: number, nodeData: ItemFlatNode) => {
        const parent = this.getParentNode(nodeData);
        if (parent) {
            return (parent.maxSelectableChildren === 1);
        }
        return false;
    }

    isVisible = (nodeData: ItemFlatNode) => (nodeData.visible !== false);

    isSelected = (node: ItemFlatNode) => (this.materialTree.checklistSelection.isSelected(node));

    someSelected = () => (this.materialTree.checklistSelection.selected.length > 0);

    hasMaxSelectableChildren = (node: ItemFlatNode) => {
        const parent = this.getParentNode(node);
        if (parent) {
            return (parent.maxSelectableChildren);
        }
        return false;
    }

    hasSelectableChildren(node: ItemFlatNode) {
        let children = 0;
        if (!node.maxSelectableChildren) {
            const descendants = this.materialTree.treeControl.getDescendants(node);
            descendants.forEach(item => {
                if (this.isNodeSelectable(node, item)) {
                    children++;
                }
            });
        }
        return (children > 0);
    }

    getChildrenSelectedCount(node: ItemFlatNode) {
        if (!this.childrenSelectedCount[node.Id]) {
            const count = this.materialTree.treeControl.getDescendants(node).filter(i => this.materialTree.checklistSelection.isSelected(i)).length;
            this.childrenSelectedCount[node.Id] = count;
        }
        return this.childrenSelectedCount[node.Id];

    }

    resetChildrenSelectedCount() {
        this.childrenSelectedCount = {};
    }

    getParentNode(node: ItemFlatNode): ItemFlatNode | null {
        const currentLevel = this.materialTree.getLevel(node);
        if (currentLevel < 1) {
            return null;
        }
        const startIndex = this.materialTree.treeControl.dataNodes.indexOf(node) - 1;
        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.materialTree.treeControl.dataNodes[i];
            if (this.materialTree.getLevel(currentNode) < currentLevel) {
                return currentNode;
            }
        }
        return null;
    }

    expandAllGroupsWithSelection() {
        for (const rowNode of this.materialTree.treeControl.dataNodes) {
            if (this.hasToExpand(rowNode)) {
                this.expandNode(rowNode);
            }
        }
    }

    hasToExpand(rowNode) {
        if (this.materialTree.checklistSelection.isSelected(rowNode)) {
            return true;
        }
        else if (rowNode.expandable) {
            const descendants = this.materialTree.treeControl.getDescendants(rowNode);
            let countSelected = 0;
            for (const childNode of descendants) {
                if (this.hasToExpand(childNode)) {
                    countSelected++;
                }
            }
            return !!countSelected;
        }
        return false;
    }

    findNodeById(guid: string): ItemFlatNode {
        return this.nodeHt[guid];
    }

    toggleGroup(node: ItemFlatNode) {
        this.materialTree.treeControl.toggleDescendants(node);
    }

    expandAll() {
        this.materialTree.treeControl.expandAll(); // Questo è molto lento
    }

    collapseAll() {
        this.materialTree.treeControl.collapseAll();
    }

    expandNode(node: ItemFlatNode) {
        this.materialTree.treeControl.expand(node);
    }

    collapseNode(node: ItemFlatNode) {
        this.materialTree.treeControl.collapse(node);
    }

    checkAll() {
        for (const rowNode of this.materialTree.treeControl.dataNodes) {
            if (!rowNode.expandable && !this.hasMaxSelectableChildren(rowNode)) { // è foglia e il padre non ha maxSelectableChildren
                this.materialTree.checklistSelection.select(rowNode);
            }
        }
        this.notifySelectedItems();
    }

    unCheckAll() {
        for (const node of this.materialTree.treeControl.dataNodes) {
            this.materialTree.checklistSelection.deselect(node);
            node.selectable = true;
        }
        this.notifySelectedItems();
    }

    unCheckGroup(node: ItemFlatNode) {
        this.materialTree.checklistSelection.deselect(node);
        node.selectable = true;
        const descendants = this.materialTree.treeControl.getDescendants(node);
        descendants.forEach(item => {
            this.materialTree.checklistSelection.deselect(item);
            item.selectable = true;
        });
        this.notifySelectedItems();
    }

    isNodeSelectable(parentNode: ItemFlatNode, node: ItemFlatNode) {
        let isSelectable = false;
        if (
            node.selectable !== false &&
            node.level === (parentNode.level + 1) &&
            (!node.expandable || node.expandable && node.checkable === true)
        ) {
            isSelectable = true;
        }
        return isSelectable;
    }

    checkGroup(node: ItemFlatNode) {
        const descendants = this.materialTree.treeControl.getDescendants(node);
        descendants.forEach(item => {
            if (this.isNodeSelectable(node, item)) {
                this.materialTree.checklistSelection.select(item);
            }
        });
        this.notifySelectedItems();
    }

    notifySelectedItems() {
        const selectedItems = this.materialTree.checklistSelection.selected.map(s => new ItemNode(s, true));
        this.selectedItemsChange.next(selectedItems);
    }

    refreshSelectedItems(selectedItems: ItemNode[]) {
        this.materialTree.treeControl.dataNodes.forEach(node => {
            const selectedItem = selectedItems.find(x => x.Id === node.Id);
            if (selectedItem) {
                this.materialTree.checklistSelection.select(node);
            } else {
                this.materialTree.checklistSelection.deselect(node);
            }
        });
        this.clearCounts();
        this.materialTree.treeControl.dataNodes.forEach(node => {
            this.setSelectedLeavesCounts(node);
        });
    }

    clearCounts() {
        this.materialTree.nodesSelectedCounts = {};
    }

    setSelectedLeavesCounts(node: ItemFlatNode) {
        if (node) {
            if (node.expandable) {
                const descendants = this.materialTree.treeControl.getDescendants(node);
                const countNodes = descendants.filter(child => this.materialTree.checklistSelection.isSelected(child)).length;
                this.materialTree.nodesSelectedCounts[node.Id] = countNodes;
            }
        }
    }

}
